import pino from 'pino';

const MAX_DEPTH = 8;

function isErrorLike(value: object): value is Error {
  // Duck-typing as well, because instanceof misses errors created in another realm
  return (
    value instanceof Error ||
    (typeof (value as Error).message === 'string' && typeof (value as Error).stack === 'string')
  );
}

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value);

  return proto === Object.prototype || proto === null;
}

function serialize(value: unknown, depth: number, ancestors: object[]): unknown {
  if (typeof value !== 'object' || value === null) {
    return value;
  }

  if (isErrorLike(value)) {
    return pino.stdSerializers.err(value);
  }

  // Cycles and very deep objects are left as they are, pino's stringifier handles them
  if (depth >= MAX_DEPTH || ancestors.includes(value)) {
    return value;
  }

  const isArray = Array.isArray(value);

  // Dates, Maps and class instances would lose their shape if we cloned them
  if (!isArray && !isPlainObject(value)) {
    return value;
  }

  const nextAncestors = [...ancestors, value];

  if (isArray) {
    return value.map(item => serialize(item, depth + 1, nextAncestors));
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, serialize(entry, depth + 1, nextAncestors)])
  );
}

/**
 * Pino `formatters.log` hook: Error fields are not enumerable, so an Error under any key or depth is logged as `{}`.
 * The top-level `err` key is left to pino's own serializer, which would otherwise overwrite `type` with `Object`.
 */
export function serializeErrors(logObject: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(logObject).map(([key, value]) => [key, key === 'err' ? value : serialize(value, 1, [logObject])])
  );
}
